import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { setBasketAttributeValue } from '@wecasa/common/src/ducks/common/basket';
import { useCheckPromotions } from '@wecasa/common/src/queries/promotions-queries';
import type { User } from '@wecasa/common/src/types/commons';

import type { CommonPromoCodeProps } from '../basket/types';

export type UsePromoCodeCheck = Pick<
  CommonPromoCodeProps,
  'totalPrice' | 'universeReference' | 'promotion' | 'cleaningPrestation'
> & { user?: User };

const usePromoCodeCheck = ({
  cleaningPrestation,
  promotion,
  totalPrice,
  universeReference,
  user,
}: UsePromoCodeCheck) => {
  const dispatch = useDispatch();
  const { error, isLoading, mutate: checkCode } = useCheckPromotions();

  const checkPromoCode = (code: string) => {
    checkCode(
      {
        code,
        universe: universeReference,
        total_price: totalPrice,
        prestations: cleaningPrestation ? [cleaningPrestation.reference] : undefined,
      },
      {
        onSuccess: (promo) => {
          dispatch(setBasketAttributeValue('promotion', promo));
        },
      },
    );
  };

  // 1. For cleaning, we want this effect to be executed only if the cleaningPrestation has changed,
  // in this case we want to check if the promo code is still valid
  // 2. we have some promo codes that are only for new users so when the user logged in we need
  // to check again that the promo code is still valid.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reason
  useEffect(() => {
    if (promotion?.code && (cleaningPrestation || user)) {
      checkPromoCode(promotion.code);
    }
  }, [cleaningPrestation, user]);

  return { error, isLoading, checkPromoCode };
};

export default usePromoCodeCheck;
